import React, { useEffect, useRef, useState } from 'react';
import { RenderStep } from '../components/FormControlRendering';
import { WidgetFormProps } from '../WidgetForm';
import { ValidationControlHelper } from '../helpers/ValidationControlHelper';
import { getResourceString } from '../../kentico/resource-strings';
import { Lidmaatschap, LidmaatschapService } from '@fnv-core/services/lidmaatschap.service';
import { VisibilityControlHelper } from '../helpers/VisibilityControlHelper';
import DOMPurify from 'dompurify';
import { ContributionHelper } from '../helpers/ContributionHelper';

export const ContributietabelLidmaatschapWidgetForm: React.FC<WidgetFormProps> = (state) => {
  
  const [lidmaatschap, setlidmaatschap] = useState<Lidmaatschap>(null);
  const [prevContributionBody, setPrevContributionBody] = useState<ContributionRateData>(null);
  const [contribution, setContribution] = useState<ContributionRate>(null);
  const [showSubscriptionCards, setShowSubscriptionCards] = useState<boolean>(false);
  const lidmaatschapService: LidmaatschapService = new LidmaatschapService();

  if(state.fields.length > 0 && state.formLoading) {
    setTimeout(() => {
      state.setFormLoading(false);
    }, 1000);
  }

  // Initial rendering of visible fields based on initial form data
  useEffect(() => {
    state.setUseDefaultNavigation(false);

    state.setCustomVisibleConditions(() => ContributionHelper.getVisibleConditionsIntroductionContribution(state));
    state.setFieldManipulations(() => ContributionHelper.getFieldManipulationsIntroductionContribution(state));

    state.initForms(state.formDefinitions ?? state.pageModel.FormDefinition);
  }, []);

  useEffect(() => {

    if(state.fields && state.fields.length > 0) {
      let formValid = true;
      state.fields.forEach(field => {
        if(field.settings.IsHiddenField !== "True") {
          if (VisibilityControlHelper.isFieldVisible(field, state.formData, state.formErrors, state.fields, state.handleNewFormData, state.customVisibleConditions)) {
            const errors = ValidationControlHelper.validateField(state.fields, field["@column"], state.formData, state.setFormData, state.handleNewFormData, !field["@allowempty"], false);
            if(errors.length > 0 || !(state.formData[field["@column"]]?.touched ?? false) && !field["@allowempty"]) {
              formValid = false;
            }
          }
        }
      });
      setShowSubscriptionCards(formValid);
    }

    if(state.formData["PersoonId"] && !lidmaatschap) {
      lidmaatschapService.GetLidmaatschapData<Lidmaatschap>(state.formData["PersoonId"].value).then((data) => {
        setlidmaatschap(data);
      })
    }

    if(ValidationControlHelper.validateField(state.fields, "Geboortedatum", state.formData, state.setFormData, state.handleNewFormData, true)?.length == 0) {
      ContributionHelper.getContribution(state, prevContributionBody, setPrevContributionBody).then((response) => {
        if(response != null) {
          setContribution(response.data);
        }
      });
    }
   
  }, [state.formData]);

  const getContributionAdvantages = () : any => {
    const contributionBlock = state.fields.filter(field => field.settings.componentidentifier === 'ContributieBlock').find(field => VisibilityControlHelper.isFieldVisible(field, state.formData, state.formErrors, state.fields, state.handleNewFormData, state.customVisibleConditions));
    if(contributionBlock) {
      return DOMPurify.sanitize(contributionBlock.settings.MembershipAdvantages, { ADD_ATTR: ['target'] });
    }
  }

  const buildQueryString = (data: Record<string, { value: string }>) => {
    const queryParams: Record<string, string> = {};
    Object.keys(data).forEach((key) => {
      const value = state.formatFormDataForSubmit(key, data[key].value);
      if(value) {
        queryParams[key] = value;
      }
    });
    return new URLSearchParams(queryParams).toString();
  };

  const handleNavigate = () => {
    const queryString = buildQueryString(state.formData);
    const url = `${state.pageModel.FormThankYou ? state.pageModel.FormThankYouPage : '/'}?${queryString}`;
    window.location.href = url;
  };

  return (
    <>
      {!state.formLoading && (
      <div className='col-12 mijn-fnv__edit-page p-0'>
        <RenderStep
          formDefinition={state.formDefinition}
          fields={state.fields}
          visibleFields={state.visibleFields} 
          currentStepIndex={state.currentStepIndex} 
          formData={state.formData} 
          setFormData={state.setFormData}
          formErrors={state.formErrors} 
          handleChange={state.handleChange} 
          handleBlur={state.handleBlur}
          formSummaryData={[]}
          navigateToStep={state.setHandleFormStepSpecific}
        />
        {showSubscriptionCards && (
          <div className="fnv-form-content fnv-form-content__membership">
            <div className="row">
              <div className="col-md-6 mb-md-0 mb-sm-3 mb-3">
                <div className="card content">
                  <div className="card-body">
                    <div className="fnv-form-content fnv-form-content__membership p-3">
                      <p className='content__intro'><b>{getResourceString("Form.Lidworden.Lidmaatschap.Label")}</b></p>
                      <div className="discount-text">
                        <span className="discount-text__highlighted">{getResourceString("Form.Lidworden.Lidmaatschap.FirstXMonths").replace(`{months}`, contribution?.monthlyPeriod?.toString() ?? "2")}</span>
                        <span className="discount-text__highlighted green">
                          €{contribution?.discountPrice?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) ?? "-.--"} p/m
                        </span>
                      </div>
                      <div className="discount-text">
                        <span>{getResourceString("Form.Lidworden.Lidmaatschap.AfterXMonths")}</span>
                        <span>
                          €{contribution?.price?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) ?? "-.--"} p/m
                        </span>
                      </div>
                      <div className="discount-badge">{getResourceString("Form.Lidworden.Lidmaatschap.DiscountPercentage").replace(`{discount}`, contribution?.discountProcentage ?? "50")}</div>
                    </div>
                  </div>
                  <div className="card-footer bg-transparent border-none p-4">
                    <button type="button" className="btn btn-success" onClick={handleNavigate}>{getResourceString("LidWorden.ContributieBlock.BecomeMemberText")}</button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="fnv-form-content fnv-form-content__information" dangerouslySetInnerHTML={{ __html: getContributionAdvantages() }}>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </>
  );
};

export default ContributietabelLidmaatschapWidgetForm;